import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { themeOf } from "@/lib/mall";
import { markSource } from "@/lib/source";

const KEY = "mall.activeStore";
const SOLO_KEY = "mall.soloSite";
const HOST_KEY = "mall.soloHost";   // this shop owns the hostname we're on — there is no mall here
const DEMO_KEY = "mall.demo";
const HOME = "the-sign-shop";   // 13thav's own brick & mortar — the shop the hallway defaults to
const TIER_LABEL = {
    kiosk: "Starter", storefront: "Storefront", department: "Pro Shop",
    standalone: "Virtual Brick & Mortar",
};
const MallContext = createContext(null);

const read = () => {
    try { return JSON.parse(sessionStorage.getItem(KEY)) || null; } catch { return null; }
};
const readSolo = () => {
    try { return localStorage.getItem(SOLO_KEY) || null; } catch { return null; }
};
/** Set only when the hostname itself belongs to a shop. Those visitors get no mall at all. */
const readByHost = () => {
    try { return !!localStorage.getItem(HOST_KEY); } catch { return false; }
};
/** The pitch page's "See it live" — one visit only, so it lives in sessionStorage. */
const readDemo = () => {
    try { return sessionStorage.getItem(DEMO_KEY) || null; } catch { return null; }
};

export function MallProvider({ children }) {
    const [floor, setFloor] = useState(null);
    const [store, setStore] = useState(read);
    const [solo, setSolo] = useState(() => {
        // A clicked-into shop no longer locks the browser; only a shop's own hostname does.
        if (readByHost()) return readSolo();
        try { localStorage.removeItem(SOLO_KEY); } catch { /* private mode */ }
        return null;
    });
    const [byHost, setByHost] = useState(readByHost);
    const [demo, setDemo] = useState(readDemo);
    const [site, setSite] = useState(null);

    useEffect(() => {
        const fallback = { anchors: [], storefronts: [], kiosks: [], classifieds: [], own_sites: [], home_store: HOME };
        const late = setTimeout(() => setFloor((f) => f || fallback), 8000);
        api.get("/stores/mall").then(({ data }) => setFloor(data)).catch(() => setFloor(fallback))
            .finally(() => clearTimeout(late));
        return () => clearTimeout(late);
    }, []);

    /** Arrived on a shop's own domain (or their 13thav subdomain)? Serve that shop, not the mall. */
    useEffect(() => {
        api.get("/tenant/resolve", { params: { host: window.location.host } })
            .then(({ data }) => {
                if (!data?.slug) return;
                try {
                    localStorage.setItem(SOLO_KEY, data.slug);
                    localStorage.setItem(HOST_KEY, data.slug);
                } catch { /* private mode */ }
                setSolo(data.slug);
                setByHost(true);
            })
            .catch(() => { /* 13thav's own host */ });
    }, []);

    useEffect(() => {
        const t = themeOf(store);
        const root = document.documentElement;
        root.style.setProperty("--store-primary", t.primary);
        root.style.setProperty("--store-secondary", t.on);
        root.style.setProperty("--store-accent", t.accent);
        if (store) sessionStorage.setItem(KEY, JSON.stringify(store));
        else sessionStorage.removeItem(KEY);
    }, [store]);

    const enter = useCallback((s, via) => {
        if (!s?.slug) return;
        markSource(via);
        setStore({
            slug: s.slug, name: s.name, city: s.city || "", tier: s.tier,
            tier_label: s.tier_label || TIER_LABEL[s.tier] || "Store", theme: themeOf(s), logo: s.logo || null,
            fulfillment_line: (s.fulfillment_line || "").trim() === (s.city || "").trim() ? "" : (s.fulfillment_line || ""), verified: !!s.verified,
            designers: !!(s.designers ?? s.designers_enabled),
        });
    }, []);

    /** Standing on a shop's own site, there is no hallway to walk back out to. */
    const leave = useCallback(() => { if (!solo && !demo) setStore(null); }, [solo, demo]);

    const enterSolo = useCallback((slug) => {
        try { localStorage.setItem(SOLO_KEY, slug); } catch { /* private mode */ }
        setSolo(slug);
    }, []);

    const exitSolo = useCallback(() => {
        try {
            localStorage.removeItem(SOLO_KEY);
            localStorage.removeItem(HOST_KEY);
            sessionStorage.removeItem(DEMO_KEY);
        } catch { /* private mode */ }
        setSolo(null);
        setByHost(false);
        setDemo(null);
        setSite(null);
        setStore(null);
    }, []);

    /** "See it live" walks the buyer into the house shop's white-labeled site. */
    const enterDemo = useCallback((slug) => {
        try { sessionStorage.setItem(DEMO_KEY, slug); } catch { /* private mode */ }
        setDemo(slug);
    }, []);

    const exitDemo = useCallback(() => {
        try { sessionStorage.removeItem(DEMO_KEY); } catch { /* private mode */ }
        setDemo(null);
        setSite(null);
        setStore(null);
    }, []);

    /** One tenant, one building: pull their whole site and stand the visitor inside it. The
     *  pitch page's demo always wins — clicking "See it live" must show the demo shop even if
     *  the visitor had wandered into somebody else's site first. */
    const siteSlug = demo || solo;
    useEffect(() => {
        if (!siteSlug) { setSite(null); return; }
        api.get(`/stores/site/${siteSlug}`).then(({ data }) => { setSite(data); enter(data.store); })
            .catch(() => {
                try { localStorage.removeItem(SOLO_KEY); sessionStorage.removeItem(DEMO_KEY); } catch { /* private mode */ }
                setSolo(null);
                setDemo(null);
            });
    }, [siteSlug, enter]);

    const homeSlug = floor?.home_store || null;
    const homeStore = useMemo(
        () => [...(floor?.own_sites || []), ...(floor?.anchors || [])].find((a) => a.slug === homeSlug) || null,
        [floor, homeSlug]);

    /** On a shop's own site the catalog is that shop's. Anywhere else, no shop is picked for you. */
    const ensureStore = useCallback(() => {
        if (store) return;
        if (siteSlug && site?.store) enter(site.store);
    }, [store, siteSlug, site, enter]);

    const value = useMemo(() => ({
        store, slug: store?.slug || null, inStore: !!store, theme: themeOf(store),
        floor, anchors: siteSlug ? [] : (floor?.anchors || []), homeSlug, homeStore,
        shopSlug: store?.slug || siteSlug || null, ensureStore, enter, leave,
        solo: siteSlug, soloByHost: byHost && !demo, site, enterSolo, exitSolo,
        demo, enterDemo, exitDemo,
    }), [store, floor, homeSlug, homeStore, ensureStore, enter, leave, siteSlug, site, byHost,
        enterSolo, exitSolo, demo, enterDemo, exitDemo]);

    return <MallContext.Provider value={value}>{children}</MallContext.Provider>;
}

export const useMall = () => useContext(MallContext) || {
    store: null, slug: null, inStore: false, theme: themeOf(null),
    floor: null, anchors: [], homeSlug: null, homeStore: null, shopSlug: null,
    ensureStore: () => { }, enter: () => { }, leave: () => { },
    solo: null, soloByHost: false, site: null, enterSolo: () => { }, exitSolo: () => { },
    demo: null, enterDemo: () => { }, exitDemo: () => { },
};
